// src/api/soxlPullbackApi.ts — SOXL Pullback (Moderate) strategy signal

import { buildApiUrl } from "./etfApi";

export interface SoxlPullbackScores {
  impulse_present: boolean;
  impulse_score: number;
  pullback_len: number;
  pullback_quality_score: number;
  confirm_score: number;
  setup_score: number;
  flags: Record<string, unknown>;
}

export interface SoxlPullbackSignalResponse {
  symbol: string;
  timestamp: string | null;
  timeframe_exec: string;
  timeframe_trend: string;
  filters: Record<string, unknown> & {
    data_ok?: boolean;
    trend_60m?: boolean;
    past_open_avoid?: boolean;
    vwap_atr_ok?: boolean;
    semis_green?: boolean;
    divergence_ok?: boolean;
    rsi_60m_over_max?: boolean;
    smh_day_pct?: number | null;
    nvda_day_pct?: number | null;
    rs_vs_qqq?: number | null;
  };
  scores: SoxlPullbackScores;
  setup: Record<string, unknown> & {
    entry_valid?: boolean;
    session_vwap?: number | null;
    session_low?: number | null;
    prior_intraday_high?: number | null;
  };
  entry: Record<string, unknown> & { price?: number; at_close?: boolean };
  stops: Record<string, unknown> & { stop_below?: number; mode?: string };
  targets: Record<string, unknown> & {
    target1?: number;
    take_profit_pct?: number;
    target_mode?: string;
    runner_trail?: string;
  };
  position_sizing: Record<string, unknown> & {
    risk_pct?: number;
    risk_per_share?: number;
    suggested_shares?: number;
    two_tranche?: boolean;
  };
  options_overlay: Record<string, unknown> & {
    trade_vehicle?: string;
    aggressiveness?: string;
    approximate?: boolean;
    note?: string;
    suggested_dte?: number;
    iv_proxy?: number;
  };
  llm_eval: Record<string, unknown> & {
    confidence_score?: number;
    rationale?: string[];
    risk_flags?: string[];
    recommended_action?: string;
  };
  next_action: string;
  data_meta: Record<string, unknown> & {
    provider?: string;
    data_timestamp?: string | null;
    bars_received?: number;
    bars_required?: number;
    error?: string;
    revision?: string;
  };
}

export interface SoxlPullbackParams {
  symbol?: string;
  semis_green_enabled?: boolean;
  divergence_skip_enabled?: boolean;
  divergence_threshold_pct?: number;
  stop_mode?: "conservative" | "moderate";
  risk_pct?: number;
  two_tranche?: boolean;
  take_profit_pct?: number;
  target_mode?: string;
  trade_vehicle?: "shares" | "defined_risk_options";
  aggressiveness?: "conservative" | "moderate" | "aggressive";
  account_value_usd?: number;
  iv_proxy?: number;
  pullback_quality_min?: number;
  confirm_score_min?: number;
}

export async function getSoxlPullbackSignal(
  params: SoxlPullbackParams = {}
): Promise<SoxlPullbackSignalResponse> {
  const p = new URLSearchParams();
  if (params.symbol != null) p.set("symbol", params.symbol);
  if (params.semis_green_enabled != null) p.set("semis_green_enabled", String(params.semis_green_enabled));
  if (params.divergence_skip_enabled != null) p.set("divergence_skip_enabled", String(params.divergence_skip_enabled));
  if (params.divergence_threshold_pct != null) p.set("divergence_threshold_pct", String(params.divergence_threshold_pct));
  if (params.stop_mode != null) p.set("stop_mode", params.stop_mode);
  if (params.risk_pct != null) p.set("risk_pct", String(params.risk_pct));
  if (params.two_tranche != null) p.set("two_tranche", String(params.two_tranche));
  if (params.take_profit_pct != null) p.set("take_profit_pct", String(params.take_profit_pct));
  if (params.target_mode != null) p.set("target_mode", params.target_mode);
  if (params.trade_vehicle != null) p.set("trade_vehicle", params.trade_vehicle);
  if (params.aggressiveness != null) p.set("aggressiveness", params.aggressiveness);
  if (params.account_value_usd != null) p.set("account_value_usd", String(params.account_value_usd));
  if (params.iv_proxy != null) p.set("iv_proxy", String(params.iv_proxy));
  if (params.pullback_quality_min != null) p.set("pullback_quality_min", String(params.pullback_quality_min));
  if (params.confirm_score_min != null) p.set("confirm_score_min", String(params.confirm_score_min));

  const url = buildApiUrl(`api/soxl_pullback/signal?${p.toString()}`);
  const res = await fetch(url);
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `HTTP ${res.status}`);
  }
  return res.json();
}
